import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, Wallet } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useSportSwitcher } from '@/hooks/useSportSwitcher';
import AccountSettingsDialog from '@/components/AccountSettingsDialog';

const UserAccountMenu = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const profile = useUserProfile();
  const { user, avatarUrl, initials } = profile;

  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // SAB-576: desktop sport switcher (shared hook). Always shown; membership decides the
  // destination (their league vs the /nfl/start chooser) and the label, not visibility.
  const { target: switchTarget, destination: switchTo, actionLabel: switchLabel } = useSportSwitcher();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleOpenEdit = () => {
    setOpen(false);
    profile.loadProfile();
    setEditOpen(true);
  };

  return (
    <div className="relative hidden lg:block" ref={menuRef}>
      <Button variant="ghost" size="icon" onClick={() => setOpen(!open)}>
        {avatarUrl ? (
          <Avatar className="h-7 w-7">
            <AvatarImage src={avatarUrl} alt="Avatar" />
            <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
          </Avatar>
        ) : (
          <User className="h-5 w-5" />
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-56 rounded-lg border border-border bg-background shadow-xl py-1">
          <div className="px-3 py-2 flex items-center gap-2">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={avatarUrl || undefined} alt="Avatar" />
              <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              {profile.displayName && <p className="text-sm font-medium truncate">{profile.displayName}</p>}
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Separator />
          <button
            onClick={() => { setOpen(false); navigate(switchTo); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
          >
            <span className="text-base leading-none">{switchTarget.emoji}</span>
            <span>{switchLabel}</span>
            <span className="ml-auto text-[9px] font-medium uppercase tracking-wider text-orange-400 border border-orange-500/30 rounded px-1 py-0.5">Beta</span>
          </button>
          <Separator />
          <button
            onClick={() => { setOpen(false); navigate('/wallet'); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
          >
            <Wallet className="h-4 w-4" />
            Wallet
          </button>
          <button
            onClick={handleOpenEdit}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
          >
            <Settings className="h-4 w-4" />
            Account Settings
          </button>
          <button
            onClick={() => { setOpen(false); signOut(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-muted transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      )}

      <AccountSettingsDialog open={editOpen} onOpenChange={setEditOpen} profile={profile} />
    </div>
  );
};

export default UserAccountMenu;
